import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Custom tailwind-merge configuration that includes HEN Design System tokens.
 * This ensures custom utilities like rounded-max, rounded-large, etc. are recognized.
 */
const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      rounded: [
        'rounded-xxs',
        'rounded-xs',
        'rounded-max',
        'rounded-button',
        'rounded-button-text-only',
        'rounded-button-icon-only',
        'rounded-badge',
        'rounded-field',
        'rounded-nav-panel',
        'rounded-popover-button-open',
        'rounded-pill-button',
      ],
      // Safe alignment has to replace justify-*, or both utilities stay and
      // stylesheet order decides which one wins.
      'justify-content': [
        '[justify-content:safe_center]',
        '[justify-content:safe_flex-end]',
      ],
      // Field focus ring widths are tokens, not lengths. Registering them as
      // ring widths lets a ring color override leave the width in place, and
      // lets ring-2 (error) and ring-0 replace them.
      'ring-w': ['ring-field-focus-width', 'ring-field-open-width'],
      // These are unregistered; tailwind-merge would read them as font families
      'font-weight': [
        'font-badge',
        'font-button',
        'font-heading-weight',
        'font-pill-button',
      ],
      'font-size': [
        // HEN typography tokens
        'text-h1',
        'text-h2',
        'text-h3',
        'text-h4',
        'text-h5',
        'text-h6',
        'text-d1',
        'text-d2',
        'text-d3',
        'text-b1',
        'text-b2',
        'text-b3',
        'text-b4',
        'text-b5',
        'text-l1',
        'text-l2',
        'text-badge-label',
        'text-badge-size',
        'text-caption',
        'text-button-xxs',
        'text-button-xs',
        'text-button-sm',
        'text-button-lg',
        // Without this, tailwind-merge reads it as a text color and drops it
        // next to `text-pill-button-fg`
        'text-pill-button',
      ],
      leading: [
        // HEN line-height tokens (must match fontSize scale so text-* + leading-* merge correctly)
        'leading-h1',
        'leading-h2',
        'leading-h3',
        'leading-h4',
        'leading-h5',
        'leading-h6',
        'leading-d1',
        'leading-d2',
        'leading-d3',
        'leading-b1',
        'leading-b2',
        'leading-b3',
        'leading-b4',
        'leading-b5',
        'leading-l1',
        'leading-l2',
        'leading-caption',
      ],
      gap: [
        // HEN spacing tokens
        'gap-min',
        'gap-xxs',
        'gap-xs',
        'gap-sm',
        'gap-md',
        'gap-lg',
        'gap-xl',
        'gap-2xl',
        'gap-3xl',
        'gap-max',
        'gap-section-break',
        'gap-pill-button',
      ],
      p: [
        // HEN spacing tokens
        'p-min',
        'p-xxs',
        'p-xs',
        'p-sm',
        'p-md',
        'p-lg',
        'p-xl',
        'p-2xl',
        'p-3xl',
        'p-max',
        'p-b4',
        'p-button-icon-only',
      ],
      px: [
        // HEN spacing tokens for horizontal padding
        'px-min',
        'px-badge-x',
        'px-badge-size-x',
        'px-xxs',
        'px-xs',
        'px-sm',
        'px-md',
        'px-lg',
        'px-xl',
        'px-2xl',
        'px-3xl',
        'px-max',
        'px-b4',
        'px-page-sides',
        'px-info-card-sides',
        'px-info-card-med-sides',
        'px-info-box-lg-sides',
        'px-button-x-xxs',
        'px-button-x-xs',
        'px-button-x-sm',
        'px-button-x-lg',
        'px-pill-button-x',
      ],
      py: [
        // HEN spacing tokens for vertical padding
        'py-min',
        'py-badge-y',
        'py-badge-size-y',
        'py-xxs',
        'py-xs',
        'py-sm',
        'py-md',
        'py-lg',
        'py-xl',
        'py-2xl',
        'py-3xl',
        'py-max',
        'py-d3',
        'py-info-card-top-bottom',
        'py-info-card-med-top-bottom',
        'py-info-box-lg-top-bottom',
        'py-button-y-xxs',
        'py-button-y-xs',
        'py-button-y-sm',
        'py-button-y-lg',
        'py-pill-button-y',
      ],
      w: [
        // HEN width tokens
        'w-subnav-column',
        'w-expert-card-img-sm',
        'w-button-icon-only',
        'w-carousel-card-sm',
        'w-carousel-card-md',
        'w-carousel-slot-sm',
        'w-carousel-slot-md',
        'w-popover',
      ],
      h: [
        // HEN height tokens
        'h-text-line',
        'h-expert-card-sm',
        'h-expert-card-img',
        'h-expert-card-img-sm',
        'h-button-sm',
        'h-button-lg',
        'h-button-text-only',
        'h-button-icon-only',
      ],
      'max-w': [
        // HEN max-width tokens
        'max-w-expert-card',
        'max-w-grid-image',
        'max-w-modal-fullscreen',
        'max-w-modal-sheet',
        'max-w-modal-wide',
        'max-w-content',
        'max-w-article-content',
        'max-w-nav-menu-content',
        'max-w-popover',
      ],
      'min-w': [
        // HEN min-width tokens
        'min-w-popover',
      ],
      z: [
        'z-sticky',
        'z-backdrop',
        'z-above-backdrop',
        'z-nav',
        'z-modal',
        'z-popover',
      ],
      // Unregistered, tailwind-merge would read this as a shadow color
      shadow: ['shadow-hover'],
      'max-h': [
        // HEN max-height tokens
        'max-h-grid-image',
      ],
      'min-h': [
        // HEN min-height tokens
        'min-h-button-xxs',
        'min-h-button-xs',
        'min-h-button-sm',
        'min-h-button-lg',
        'min-h-badge-size',
      ],
      // HEN spacing tokens for single-side spacing
      mt: [
        'mt-min',
        'mt-xxs',
        'mt-xs',
        'mt-sm',
        'mt-md',
        'mt-lg',
        'mt-xl',
        'mt-2xl',
        'mt-3xl',
        'mt-max',
      ],
      pt: [
        'pt-min',
        'pt-xxs',
        'pt-xs',
        'pt-sm',
        'pt-md',
        'pt-lg',
        'pt-xl',
        'pt-2xl',
        'pt-3xl',
        'pt-max',
      ],
      pb: [
        'pb-min',
        'pb-xxs',
        'pb-xs',
        'pb-sm',
        'pb-md',
        'pb-lg',
        'pb-xl',
        'pb-2xl',
        'pb-3xl',
        'pb-max',
      ],
      pl: [
        'pl-min',
        'pl-xxs',
        'pl-xs',
        'pl-sm',
        'pl-md',
        'pl-lg',
        'pl-xl',
        'pl-2xl',
        'pl-3xl',
        'pl-max',
      ],
      // Unregistered, tailwind-merge reads border-button as a border color
      'border-w': ['border-button', 'border-radio-card-selected'],
      // Unregistered, tailwind-merge ignores non-length underline offsets
      'underline-offset': [
        'underline-offset-button',
        'underline-offset-button-link',
      ],
      // Unregistered, tailwind-merge reads these Button token utilities as colors
      'text-decoration-thickness': [
        'decoration-button',
        'decoration-button-hover',
        'decoration-button-link',
        'decoration-button-hover-link',
        'decoration-link-thickness',
        'decoration-link-thickness-hover',
      ],
      // Unregistered, tailwind-merge only knows numeric and arbitrary insets
      'inset-x': ['inset-x-md'],
      // Unregistered, tailwind-merge only knows numeric and arbitrary insets
      top: ['top-sm'],
      size: [
        'size-button-icon',
        'size-button-icon-label',
        'size-button-icon-only-icon',
        'size-badge-size-icon',
        'size-pill-button-icon',
      ],
      // Unregistered, tailwind-merge reads this as a stroke color and drops
      // the Icon's `stroke-current`
      'stroke-w': ['stroke-button-icon'],
      opacity: ['opacity-button-disabled', 'opacity-tab-button-disabled'],
      'outline-w': ['outline-button'],
      'outline-offset': ['outline-offset-button'],
      transition: ['transition-button', 'transition-link'],
      duration: [
        'duration-button',
        'duration-button-gradient',
        'duration-navbar',
        'duration-link',
      ],
      ease: ['ease-button', 'ease-link'],
      // Unregistered, tailwind-merge reads this as a background color
      'bg-image': ['bg-button-gradient-primary'],
    },
  },
});

/**
 * Combines class names using clsx and merges Tailwind CSS classes with tailwind-merge.
 * This utility handles conditional classes and resolves Tailwind class conflicts.
 * Configured to recognize HEN Design System custom utilities.
 *
 * @param inputs - Class values to combine and merge
 * @returns Merged class string
 *
 * @example
 * cn('px-2 py-1', condition && 'bg-blue-500')
 * cn('px-2', 'px-4') // Returns 'px-4' (tailwind-merge resolves conflict)
 * cn('rounded-md', 'rounded-max') // Returns 'rounded-max' (HEN custom utility)
 */
export const cn = (...inputs: ClassValue[]): string => {
  return customTwMerge(clsx(inputs));
};
